import React from "react"
import { Check } from "lucide-react"

interface UserChoiceCardProps {
  title: string
  description: string
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>
  selected: boolean
  onClick: () => void
  iconClassName?: string
  variant?: "modal" | "onboarding"
}

const VARIANT_CLASSNAME: Record<NonNullable<UserChoiceCardProps["variant"]>, {
  button: { selected: string; idle: string }
  icon: { selected: string; idle: string }
  title: string
  description: string
}> = {
  modal: {
    button: {
      selected: "border-golden bg-golden/10 shadow-sm",
      idle: "border-gray-200 hover:border-gray-300",
    },
    icon: {
      selected: "bg-golden/20",
      idle: "bg-gray-100",
    },
    title: "text-my-black text-sm font-bold",
    description: "mt-0.5 text-xs text-gray-500",
  },
  onboarding: {
    button: {
      selected: "bg-golden/20 border-golden",
      idle: "bg-white/10 border-white/20 hover:bg-white/15",
    },
    icon: {
      selected: "bg-golden/30",
      idle: "bg-white/10",
    },
    title: "font-bold text-golden",
    description: "text-sm text-golden/70",
  },
}

const UserChoiceCard: React.FC<UserChoiceCardProps> = ({
  title,
  description,
  Icon,
  selected,
  onClick,
  iconClassName = "text-golden",
  variant = "modal",
}) => {
  const appearance = VARIANT_CLASSNAME[variant]

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex w-full items-center gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-golden ${selected ? appearance.button.selected : appearance.button.idle}`.trim()}
    >
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${selected ? appearance.icon.selected : appearance.icon.idle}`.trim()}
      >
        <Icon className={`h-5 w-5 ${iconClassName}`.trim()} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={appearance.title}>{title}</p>
        <p className={appearance.description}>{description}</p>
      </div>
      {variant === "modal" ? (
        <div
          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-golden bg-golden" : "border-gray-300"}`.trim()}
        >
          {selected ? <div className="h-2.5 w-2.5 rounded-full bg-white" /> : null}
        </div>
      ) : selected ? (
        <Check className="h-5 w-5 flex-shrink-0 text-golden" />
      ) : null}
    </button>
  )
}

export default UserChoiceCard